import type { EnrichedOrder } from "@/lib/orders/types";

type ErrorBody = {
  code?: string;
  status?: number;
  message?: string;
};

export class ApiError extends Error {
  code: string;
  status: number;

  constructor(code: string, status: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.status = status;
  }
}

async function readJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

export async function getOrderByPaymentId(
  providerPaymentId: string,
  signal?: AbortSignal,
): Promise<EnrichedOrder | null> {
  const trimmed = providerPaymentId.trim();
  if (!trimmed) {
    throw new ApiError("INVALID_PAYMENT_ID", 400, "Invalid payment id");
  }

  const response = await fetch(`/api/orders/by-payment/${encodeURIComponent(trimmed)}`, {
    headers: { Accept: "application/json" },
    signal,
  });

  const body = await readJson(response);
  if (response.status === 404) return null;

  if (!response.ok) {
    const error = (body ?? {}) as ErrorBody;
    throw new ApiError(
      error.code || "ORDER_LOOKUP_FAILED",
      typeof error.status === "number" ? error.status : response.status,
      error.message || "Failed to load order",
    );
  }

  return body as EnrichedOrder;
}

export type { EnrichedOrder } from "@/lib/orders/types";
